import { fetchWithAuth } from "../config/axios";
import {
    Comment,
    NewTicketData,
    RemoteComment,
    RemoteTicket,
    Ticket,
    toLocalComment,
    toLocalTicket,
} from "../models/cms";

const GOOGLE_STORAGE_URL = "https://storage.googleapis.com/";

function isGoogleUrl(url: string): boolean {
    return url.includes(GOOGLE_STORAGE_URL);
}

export async function fetchFromGoogleBucket(path: string): Promise<Blob> {
    const response = await fetchWithAuth.get(`/google/file/${path}`, { responseType: "blob" });
    return response.data;
}

export async function getAllTickets(): Promise<Ticket[]> {
    const response = await fetchWithAuth.get<RemoteTicket[]>(
        "/tickets?populate=comments,attachments",
    );
    return response.data.map((ticket: RemoteTicket) => toLocalTicket(ticket));
}

export async function getTicket(id: string): Promise<Ticket | null> {
    const response = await fetchWithAuth.get<{ data: RemoteTicket }>(`/tickets/${id}`);

    if (response.status !== 200) {
        throw new Error("Failed to get ticket");
    }

    return toLocalTicket(response.data.data);
}

export async function newTicket({
    title,
    description,
    clientId,
    author,
    useCsmInbox = false,
    assignedTo,
    attachments,
}: NewTicketData): Promise<Ticket> {
    const formData = new FormData();

    if (attachments) {
        attachments.forEach((file) => {
            formData.append("files", file);
        });
    }

    const ticketCreateBody = {
        title,
        description,
        useCsmInbox,
        assignedTo,
        author,
        client: parseInt(clientId, 10),
    };

    formData.append("data", JSON.stringify(ticketCreateBody));

    const response = await fetchWithAuth.post<{ data: RemoteTicket }>("/tickets", formData);

    if (response.status !== 201) {
        throw new Error("Failed to create ticket");
    }

    return toLocalTicket(response.data.data);
}

export async function updateTicket(ticketId: string, body: Partial<Ticket>): Promise<void> {
    const response = await fetchWithAuth.put(
        `/tickets/${ticketId}`,
        JSON.stringify({ data: body }),
    );

    if (response.status !== 200) {
        throw new Error("Could not update ticket");
    }
}

export async function closeTicket(ticketID: string): Promise<void> {
    const response = await fetchWithAuth.post(`/tickets/${ticketID}/close`);

    if (response.status !== 200 && response.status !== 201) {
        throw new Error("Failed to close ticket");
    }
}

export async function createComment(
    ticketId: string,
    text: string,
    sender: string,
    attachments?: File[],
): Promise<Comment> {
    const formData = new FormData();

    if (attachments) {
        attachments.forEach((file) => {
            formData.append("files", file);
        });
    }

    const commentCreateBody = {
        text,
        sender,
        new: false,
        tickets: [parseInt(ticketId, 10)],
    };

    formData.append("data", JSON.stringify(commentCreateBody));

    const response = await fetchWithAuth.post<{ data: RemoteComment }>(
        `/tickets/${ticketId}/comments`,
        formData,
    );

    if (response.status !== 201) {
        throw new Error("Failed to create comment");
    }

    return toLocalComment(response.data.data);
}

export async function updateComment(commentId: string, body: Partial<Comment>): Promise<void> {
    const response = await fetchWithAuth.put(
        `/tickets/comments/${commentId}`,
        JSON.stringify({ data: body }),
    );

    if (response.status !== 200) {
        throw new Error("Failed to update comment");
    }
}

export async function deleteGeneration(id: number): Promise<void> {
    const response = await fetchWithAuth.delete(`/cms/generations/${id}`);

    if (response.status !== 200) {
        throw new Error("Could not delete generation");
    }
}

/**
 * Fetches portal propaganda data from the API.
 */
export async function fetchPortalProperganda(): Promise<string> {
    const url = new URL(import.meta.env.VITE_CHORIPAN_URL + "/cms/portal-properganda");

    try {
        const response = await fetchWithAuth(url.toString());

        return response.data.data.attributes.url;
    } catch (error) {
        console.error("Error fetching portal propaganda:", error);
        throw error;
    }
}

export async function fetchFeatureFlags(paginate = false, pageSize = 100): Promise<any> {
    if (!paginate) {
        const url = new URL(import.meta.env.VITE_CHORIPAN_URL + "/cms/feature-flags");
        try {
            return await fetchWithAuth(url.toString());
        } catch (error) {
            console.error("Error fetching feature flags:", error);
            throw error;
        }
    }

    // Handle pagination if requested
    const allData: any[] = [];
    let page = 1;
    let totalPages;

    try {
        do {
            const url = new URL(
                `${import.meta.env.VITE_CHORIPAN_URL}/cms/feature-flags?pagination[page]=${page}&pagination[pageSize]=${pageSize}`,
            );
            const request = await fetchWithAuth(url.toString());
            allData.push(...request.data.data);
            page++;
            totalPages = request.data.meta.pagination.pageCount;
        } while (page <= totalPages);

        return { data: allData };
    } catch (error) {
        console.error("Error fetching feature flags with pagination:", error);
        throw error;
    }
}

export async function uploadPublicImage(file: File): Promise<string> {
    const formData = new FormData();
    formData.append("file", file);

    const response = await fetchWithAuth.post("/public/file", formData);

    if (response.status !== 200 && response.status !== 201) {
        throw new Error("Failed to upload image");
    }

    const fileId = response.data;
    return `${import.meta.env.VITE_CHORIPAN_URL}/public/file/${fileId}`;
}

export async function requestFile(fullURL: string): Promise<Blob> {
    if (isGoogleUrl(fullURL)) {
        const url = fullURL.replace(GOOGLE_STORAGE_URL, "");
        return await fetchFromGoogleBucket(url);
    } else {
        const response = await fetch(fullURL);
        return response.blob();
    }
}
